/**
 * AccommodationBlock — Wave 3 Money Layer · Task 5 (Feb 2026).
 *
 * Renders first-year student-accommodation summary for the target
 * uni: typical weekly rent (mid-tier en-suite, self-catered),
 * first-year guarantee status, contract length, catering, deadline,
 * and a source citation.
 *
 * Fetches from `/api/money/accommodation?uni_id=…`. Silent when the
 * uni isn't in the registry (avoids "we don't know" empty state
 * cluttering the Cost cluster).
 *
 * Mounted inside `TotalCourseAndLivingCost.jsx` below the monthly
 * cost-of-living chips.
 */
import { useEffect, useState } from "react";
import { Home, ShieldCheck, ShieldOff, CalendarClock, Utensils, ExternalLink } from "lucide-react";
import { api } from "@/lib/api";

export default function AccommodationBlock({ degreeId }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!degreeId) return;
    const uniId = degreeId.split("__")[0];
    if (!uniId) return;
    api.get(`/money/accommodation?uni_id=${encodeURIComponent(uniId)}`)
      .then((r) => setData(r.data?.available ? r.data : null))
      .catch(() => setData(null));
  }, [degreeId]);

  if (!data) return null;

  const {
    weekly_rent_gbp, weekly_rent_range, contract_weeks,
    first_year_guarantee, catering_included, application_deadline,
    source_url, cite, notes,
  } = data;

  // First-year guarantee visual state.
  const guaranteeMeta = first_year_guarantee === true
    ? { icon: ShieldCheck, tone: "text-emerald-700 bg-emerald-50 border-emerald-200",
        label: "First-year guarantee: yes" }
    : first_year_guarantee === "insurance"
    ? { icon: ShieldCheck, tone: "text-amber-700 bg-amber-50 border-amber-200",
        label: "Guaranteed for firm & insurance-choice applicants" }
    : { icon: ShieldOff, tone: "text-rose-700 bg-rose-50 border-rose-200",
        label: "No first-year guarantee — first-come, first-served" };

  const annualEstimate = weekly_rent_gbp && contract_weeks
    ? weekly_rent_gbp * contract_weeks
    : null;

  return (
    <div
      data-testid="accommodation-block"
      className="mt-5 pt-4 border-t border-stone-100"
    >
      <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
        <div className="text-[11px] uppercase tracking-wider text-stone-500 font-semibold flex items-center gap-1.5">
          <Home className="h-3 w-3 text-[#4C1D95]" aria-hidden />
          Halls at this university (2025-26 published prices)
        </div>
        {annualEstimate != null && (
          <div className="text-[10px] text-stone-400 font-mono">
            ≈ £{annualEstimate.toLocaleString()} for a {contract_weeks}-wk contract
          </div>
        )}
      </div>

      <div className="grid sm:grid-cols-2 gap-3">
        {/* Weekly rent tile */}
        <div
          data-testid="accommodation-rent"
          className="rounded-md border border-stone-200 bg-stone-50 px-3 py-2.5"
        >
          <div className="text-[10px] font-underground-sc tracking-wider text-stone-500">
            Typical weekly rent
          </div>
          <div className="mt-0.5 font-editorial text-xl text-[#002147] tabular-nums">
            £{weekly_rent_gbp}
            <span className="ml-1 text-xs font-mono text-stone-500">/wk</span>
          </div>
          {weekly_rent_range && (
            <div className="mt-0.5 text-[11px] text-stone-500">
              Range: {weekly_rent_range}
            </div>
          )}
          <div className="mt-1 text-[10px] text-stone-500 flex items-center gap-1">
            <Utensils className="h-3 w-3 text-stone-400" aria-hidden />
            {catering_included ? "Meals included" : "Self-catered"}
          </div>
        </div>

        {/* Guarantee tile */}
        <div
          data-testid="accommodation-guarantee"
          className={"rounded-md border px-3 py-2.5 " + guaranteeMeta.tone}
        >
          <div className="text-[10px] font-underground-sc tracking-wider opacity-70">
            First-year guarantee
          </div>
          <div className="mt-0.5 flex items-start gap-1.5">
            <guaranteeMeta.icon className="h-4 w-4 mt-0.5 shrink-0" aria-hidden />
            <span className="text-[13px] font-medium leading-snug">
              {guaranteeMeta.label}
            </span>
          </div>
          {application_deadline && (
            <div className="mt-1 text-[11px] flex items-center gap-1 opacity-80">
              <CalendarClock className="h-3 w-3" aria-hidden />
              Apply by: {application_deadline}
            </div>
          )}
        </div>
      </div>

      {notes && (
        <p className="mt-2 text-[11px] text-stone-500">{notes}</p>
      )}

      <div className="mt-2 text-[10px] text-stone-500">
        Source:{" "}
        {source_url ? (
          <a
            href={source_url}
            target="_blank"
            rel="noopener noreferrer"
            className="underline hover:text-[#4C1D95]"
            data-testid="accommodation-source-link"
          >
            {cite} <ExternalLink className="inline h-2.5 w-2.5" />
          </a>
        ) : (
          cite
        )}
      </div>
    </div>
  );
}
